import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { STATE_STYLE, SEGMENT_STYLE, canViewAI } from "../lib/states";
import LeadDrawer from "../components/LeadDrawer";
import { Flame, Search, Star, Loader2, Check } from "lucide-react";

function AiCard({ l, onClick, onPromote, onAck, canEdit, promoting, acking }) {
  return (
    <div onClick={onClick} data-testid={`ai-star-card-${l.id}`}
      className="bg-white border border-amber-200 ring-1 ring-amber-100 rounded-xl p-4 hover:shadow-md transition-all cursor-pointer">
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium text-stone-900 flex items-center gap-1.5">
          <Star className="h-4 w-4 text-amber-500 fill-amber-500" /> {l.name}
        </p>
        <span className={`text-xs px-2 py-0.5 rounded-full border ${STATE_STYLE[l.state]?.badge}`}>{l.state}</span>
      </div>
      <p className="text-xs text-stone-500 mt-1">+91 {l.phone}</p>
      <div className="flex items-center gap-2 mt-2">
        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${SEGMENT_STYLE[l.segment] || SEGMENT_STYLE.Unknown}`}>{l.segment || "Unknown"}</span>
        {l.assignee_name && <span className="text-xs text-stone-400">{l.assignee_name}</span>}
      </div>
      {l.ai_hot_reason && <p className="text-xs text-amber-700 mt-2 italic">&ldquo;{l.ai_hot_reason}&rdquo;</p>}

      <div className="mt-3 grid grid-cols-2 gap-2">
        <button onClick={(e) => { e.stopPropagation(); onAck(l); }} disabled={acking}
          data-testid={`ai-star-ack-${l.id}`}
          className="flex items-center justify-center gap-1.5 text-xs font-medium bg-stone-50 hover:bg-stone-100 text-stone-700 border border-stone-200 rounded-md py-1.5 transition-colors disabled:opacity-60">
          {acking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="h-3.5 w-3.5" /> Mark as read</>}
        </button>
        {canEdit ? (
          <button onClick={(e) => { e.stopPropagation(); onPromote(l); }} disabled={promoting}
            data-testid={`ai-star-promote-${l.id}`}
            className="flex items-center justify-center gap-1.5 text-xs font-medium bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-md py-1.5 transition-colors disabled:opacity-60">
            {promoting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Flame className="h-3.5 w-3.5" /> Mark as Hot</>}
          </button>
        ) : <span />}
      </div>
    </div>
  );
}

export default function AiStars() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [active, setActive] = useState(null);
  const [query, setQuery] = useState("");
  const [promoting, setPromoting] = useState(null);
  const [acking, setAcking] = useState(null);
  const canEdit = ["admin", "funnel_manager", "store_manager"].includes(user?.role);
  const allowed = canViewAI(user);

  const load = () => {
    if (!allowed) return;
    api.get("/leads/hot/ai-suggested")
      .then((r) => setLeads(r.data))
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const match = (l) => !query || l.name?.toLowerCase().includes(query.toLowerCase()) || l.phone?.includes(query);
  const byNewest = (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0);
  const list = leads.filter(match).sort(byNewest);

  const promote = async (l) => {
    setPromoting(l.id);
    try {
      await api.post(`/leads/${l.id}/hot`, { is_hot: true, note: "Promoted from AI Star" });
      toast.success(`${l.name} marked as Hot Lead`);
      // Also remove from AI list optimistically — a promoted lead moves to /hot.
      setLeads((cur) => cur.filter((x) => x.id !== l.id));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setPromoting(null); }
  };

  const ack = async (l) => {
    setAcking(l.id);
    try {
      await api.post(`/leads/${l.id}/ai-hot/ack`);
      setLeads((cur) => cur.filter((x) => x.id !== l.id));
      toast.success(`${l.name} marked as read`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setAcking(null); }
  };

  if (!allowed) {
    return <div className="p-6 text-sm text-stone-500">Not available for your role.</div>;
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="h-16 shrink-0 px-4 sm:px-6 flex items-center justify-between border-b border-stone-200">
        <div className="flex items-center gap-2 min-w-0">
          <Star className="h-5 w-5 text-amber-500 fill-amber-500 shrink-0" />
          <div className="min-w-0">
            <h1 className="font-heading text-xl tracking-tight text-stone-900">AI Stars</h1>
            <p className="text-xs text-stone-500 truncate">
              {list.length} unattended · AI-flagged high-potential leads (architects, bulk/B2B, high-profile).
              <span className="text-stone-400"> Acknowledged leads and Lost/Junk/RNR are hidden.</span>
            </p>
          </div>
        </div>
        <div className="relative shrink-0">
          <Search className="h-4 w-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…" data-testid="ai-star-search"
            className="pl-9 pr-3 py-2 text-sm border border-stone-300 rounded-md bg-white w-36 sm:w-56 focus:outline-none focus:ring-2 focus:ring-stone-900/60" />
        </div>
      </div>

      <div className="p-4 sm:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((l) => (
            <AiCard key={l.id} l={l} onClick={() => setActive(l)}
              onPromote={promote} onAck={ack} canEdit={canEdit}
              promoting={promoting === l.id} acking={acking === l.id} />
          ))}
          {list.length === 0 && (
            <p className="text-sm text-stone-400 col-span-full py-12 text-center">
              No AI Stars right now. New high-potential leads appear here as they get enriched.
            </p>
          )}
        </div>
      </div>

      {active && <LeadDrawer leadId={active.id} onClose={() => setActive(null)} onSaved={load} />}
    </div>
  );
}
